import React from 'react';
import { Grid, Typography } from '@mui/material';
import { Button, Drawer } from '@biq/apex-ui';
import { useNavigate } from 'react-router-dom';
import { MemoReadOnlyJsonEditor } from '../../../components';
import { DICTIONARY_PATHS } from '../../../constants/dictionary-paths';
import { EDGE_DEFINITION_PATHS } from '../../../constants/edge-definition-paths';
import { SINGLE_NAVIGATION_PATHS } from '../../../constants/single-navigation-paths';
import { WALLETS_AND_GROUPS_PATHS } from '../../../constants/wallets-and-groups-paths';
import { GLOSSARY_METADATA_PATHS } from '../../../constants/glossary-metadata-paths';
import { jobDetailsDrawerStyle } from '../../job-manager/components/job-manager-details/job-manager-details-helper';
import {
    DrawerItem,
    metadataDrawerItemType,
    metadataDrawerStyles,
    metadataEditButtonStyle,
    truncateDrawerItemTitleWithEllipses,
} from './metadata-components.helper';

interface MetadataSearchDrawerProps {
    onDrawerClose: () => void;
    item: DrawerItem;
}

export const MetadataSearchDrawer = ({
    onDrawerClose,
    item,
}: MetadataSearchDrawerProps): React.JSX.Element => {
    const navigate = useNavigate();

    const displayEditButton = (): boolean => {
        return (
            item.type !== metadataDrawerItemType.terms &&
            item.type !== metadataDrawerItemType.dataPipelines
        );
    };

    const handleButtonClick = (): void => {
        let editUrl = '';
        switch (item.type) {
            case metadataDrawerItemType.dictionaries:
                editUrl = `${DICTIONARY_PATHS.LANDING_PAGE}/${item.value?.name}`;
                break;
            case metadataDrawerItemType.edgeDefs:
                editUrl = `${EDGE_DEFINITION_PATHS.LANDING_PAGE}/${item.value?.id}`;
                break;
            case metadataDrawerItemType.userGroups:
                editUrl = `${WALLETS_AND_GROUPS_PATHS.EDIT_USER_GROUP_PATH}/${item.value?.id}`;
                break;
            case metadataDrawerItemType.userWallets:
                editUrl = `${WALLETS_AND_GROUPS_PATHS.EDIT_USER_WALLET_PATH}/${item.value?.id}`;
                break;
            case metadataDrawerItemType.systemWallets:
                editUrl = `${WALLETS_AND_GROUPS_PATHS.EDIT_SYSTEM_WALLET_PATH}/${item.value?.id}`;
                break;
            case metadataDrawerItemType.glossaries:
                editUrl = `${GLOSSARY_METADATA_PATHS.LANDING_PAGE}/${item.value?.id}`;
                break;
        }

        if (editUrl) {
            const url = new URL(window.location.origin);

            // set search param to possibly allow the user return to the metadata search from other pages.
            url.searchParams.set(
                SINGLE_NAVIGATION_PATHS.METADATA_SEARCH,
                SINGLE_NAVIGATION_PATHS.METADATA_SEARCH
            );

            navigate(editUrl + url.search, { replace: true });
        }
    };

    const subheader: React.JSX.Element = (
        <Grid sx={jobDetailsDrawerStyle.subheaderContainer}>
            <Grid sx={metadataDrawerStyles.subheaderInnerContainer}>
                <Typography style={jobDetailsDrawerStyle.subheaderStyle}>
                    {truncateDrawerItemTitleWithEllipses(item.title)}
                </Typography>
            </Grid>
            {displayEditButton() && (
                <Grid sx={metadataDrawerStyles.buttonContainer}>
                    <Button
                        size='small'
                        variant='contained'
                        color='primary'
                        style={metadataEditButtonStyle}
                        onClick={handleButtonClick}
                    >
                        Edit
                    </Button>
                </Grid>
            )}
        </Grid>
    );

    return (
        <Drawer
            closable
            open={!!item}
            drawerType='rounded'
            anchor='right'
            sx={jobDetailsDrawerStyle.detailsDrawer}
            onClose={onDrawerClose}
            title={item.type}
            titleStyles={jobDetailsDrawerStyle.titleStyle}
            subheaderChildren={subheader}
        >
            <Grid sx={metadataDrawerStyles.jsonContainer}>
                <MemoReadOnlyJsonEditor jsonData={item.value} />
            </Grid>
        </Drawer>
    );
};
